import { MouseEvent, KeyboardEvent, memo } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import {
  createSelectIsJoiningRoomById,
  selectCurrentRoomId,
} from "store/rooms";
import { IRoom } from "types";
import { makeSelectUserRoomStatusByRoomId } from "modules/room/redux/selectors";
import { useI18n } from "i18n";
import { makeSelectUsersById } from "modules/event/usersInEvent/selectors";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { isRoomFull } from "helpers/roomHelper";
import { selectIsMobile } from "modules/deviceInfo";
import { getUserName } from "modules/userProfile";
import { useStyles } from "./styles";

interface Props {
  room: IRoom;
  outlined?: boolean;
  handleMobileTap: (e: MouseEvent) => void;
  handleRightClick: (e: MouseEvent) => void;
  handleDoubleClick: (e: MouseEvent | KeyboardEvent) => void;
  handleMouseDown: (e: MouseEvent) => void;
  handleMouseUp: (e: MouseEvent) => void;
  handleHover: (isHovering: boolean) => void;
}

const RoomClickableArea = ({
  room,
  outlined,
  handleMobileTap,
  handleRightClick,
  handleDoubleClick,
  handleMouseDown,
  handleMouseUp,
  handleHover,
}: Props) => {
  const { boundaryPolygon, id, name } = room;
  const styles = useStyles();
  const isMobileView = useSelector(selectIsMobile);
  const isJoiningThisRoom = useSelector(createSelectIsJoiningRoomById(id));
  const { t } = useI18n(["conversation"]);

  const currentRoomId = useSelector(selectCurrentRoomId);
  const usersInside = useSelector(makeSelectUserRoomStatusByRoomId(id));
  const usersInRoom = useSelector(
    makeSelectUsersById(usersInside ? Object.keys(usersInside) : []),
  );

  const { eventCompanyPlan } = useCompanyPlanSettings();
  const { isFull } = isRoomFull(room, eventCompanyPlan);

  const getUserProfileName = (userId: string) =>
    getUserName(usersInRoom.get(userId));

  const getUsersNames = () =>
    usersInside
      ? Object.keys(usersInside)
          .map((userId) => getUserProfileName(userId))
          .join(", ")
      : "";

  const getAlertAriaLabel = () =>
    currentRoomId === id
      ? `${t("a11y.table.joined", { tableName: name })} `
      : "";

  const getTableAriaLabel = () =>
    usersInRoom && usersInRoom.size > 0
      ? `${t("a11y.table.has.people.in", {
          tableName: name,
          usersInsideNumber: usersInRoom.size,
        })} ${t("a11y.people.in.table")}${getUsersNames()}.`
      : t("a11y.table.empty", {
          tableName: name,
        });

  const getAriaLabelEndText = () => {
    if (currentRoomId === id) {
      return t("a11y.you.are.in.table", { tableName: name });
    }
    if (isFull) {
      return t("a11y.cant.join.table.full", { tableName: name });
    }
    return t("a11y.can.join.table", { tableName: name });
  };

  const getAriaLabel = () =>
    `${getAlertAriaLabel()}${getTableAriaLabel()} ${getAriaLabelEndText()}`;

  const commonProps = {
    className: classNames(styles.clickableArea, {
      [styles.mobileJoinRoom]: isMobileView && isJoiningThisRoom,
      [styles.outlined]: outlined,
    }),
    onClick: isMobileView ? handleMobileTap : undefined,
    onMouseUp: isMobileView ? undefined : handleMouseUp,
    onMouseDown: isMobileView ? undefined : handleMouseDown,
    onDoubleClick: isMobileView ? undefined : handleDoubleClick,
    onMouseEnter: isMobileView ? undefined : () => handleHover(true),
    onMouseLeave: isMobileView ? undefined : () => handleHover(false),
    onTouchStart: isMobileView ? () => handleHover(true) : undefined,
    onTouchEnd: isMobileView ? () => handleHover(false) : undefined,
    onContextMenu: handleRightClick,
    role: currentRoomId === id ? "alert" : undefined,
    "aria-label": getAriaLabel(),
    "data-dd-action-name": "Table",
  };

  if (boundaryPolygon) {
    return (
      <svg width="100%" height="100%">
        <polygon
          stroke="none"
          data-testid="polygon"
          fill="transparent"
          points={boundaryPolygon}
          {...commonProps}
        />
      </svg>
    );
  }
  return <div data-testid="div" {...commonProps} />;
};

export default memo(RoomClickableArea);
